import { useAuth0 } from "@auth0/auth0-react";
import { ChatBubbleLeftIcon, PencilSquareIcon, PlayCircleIcon, TrashIcon } from "@heroicons/react/24/outline";
import { downloadFileFromUrl } from "@utils/helpers";
import clsx from "clsx";
import { format, isBefore, subMinutes } from "date-fns";
import React, { useEffect, useRef, useState } from "react";

import { Button, Paragraph } from "@fronterahealth/frontera-ui-components";
import {
  PopoverMenu,
  PopoverMenuItem,
} from "@fronterahealth/frontera-ui-components/PopoverActionMenu/PopoverActionMenu";

import { ApiMediaVideoUploadStatusChoices, ChatMessageType } from "@api/graphql/types-and-hooks";
import { ConfirmationDialog } from "@components/ConfirmationDialog/ConfirmationDialog";
import { UserInitialsView } from "@components/UserInitial/UserInitialsView";
import { ChatAttachment } from "@pages/FronteraClarity/components/ChatAttachment";
import { ChatWYSIWYG } from "@pages/FronteraClarity/components/ChatWYSIWYG";
import { parseMediaCommentsCount, resolveEchoSubheader } from "@pages/FronteraClarity/components/chatBubbleLogic";
import { useBreakpoint } from "@pages/VideoSessionsV2/hooks/useMediaMatcher";

interface LiveChatMessageProps {
  message: ChatMessageType;
  handleEdit: (chatMessageId: string, editedMessage: string, onSuccess: () => void) => void;
  handleDelete: (deleteMessageId: string, onSuccess: () => void) => void;
  isLastMessage?: boolean;
  /** Called when the user taps the video thumbnail or an echo bubble
   * subheader. Wires into the video detail panel.
   * `seekTime` and `commentId` are only set when clicking an echo
   * bubble — the overlay will seek to that timestamp and highlight
   * the matching comment on open. */
  onVideoClick?: (mediaId: string, seekTime?: number, commentId?: string) => void;
}

export const LiveChatMessage: React.FC<LiveChatMessageProps> = ({
  message,
  handleEdit,
  handleDelete,
  isLastMessage,
  onVideoClick,
}) => {
  const { isMobile } = useBreakpoint();

  const [openDeleteMessage, setOpenDeleteMessage] = useState<boolean>(false);
  const [deleteMessageId, setDeleteMessageId] = useState<string>("");
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editedMessage, setEditedMessage] = useState<string>(message.content || "");

  // update edited message state
  useEffect(() => {
    if (message.content) {
      setEditedMessage(message.content);
    }
  }, [message.content]);

  const { user } = useAuth0();
  const editContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isEditing) {
      setTimeout(() => {
        editContainerRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }, 50);
    }
  }, [isEditing]);

  // verify which messages are from the current auth user
  const isUser = message?.user?.username == user?.sub;
  const userFirstName = message.userFirstName;
  const userLastName = message.userLastName;

  const handleDownloadFile = async () => {
    if (!message.file) return;

    try {
      if (message.file.signedS3LearnerFile) {
        await downloadFileFromUrl({
          url: message.file.signedS3LearnerFile,
          fileName: message.file.originalFileName,
        });
      }
    } catch (error) {
      console.error("Error downloading file:", error);
    }
  };

  const menuItems: PopoverMenuItem[] = [
    {
      label: "Edit",
      Icon: PencilSquareIcon,
      callback: () => {
        setIsEditing(true);
      },
    },
    {
      label: "Delete",
      Icon: TrashIcon,
      textColorClassName: "text-red-600",
      callback: () => {
        setDeleteMessageId(message.id);
        setOpenDeleteMessage(true);
      },
    },
  ];

  const isOlderThan15min = isBefore(new Date(message.createdDate), subMinutes(new Date(), 15));

  // Video attachment (chat-sourced upload). Only show once FDM marks it COMPLETED.
  const mediaVideo =
    message.media?.videoUploadStatus === ApiMediaVideoUploadStatusChoices.Completed ? message.media : null;
  const mediaCommentsCount = parseMediaCommentsCount(mediaVideo?.commentsCount ?? null);

  // Echo bubble (role=COMMENT): subheader shows "Comment on video @ 4:45" etc.
  const mc = message.mediaComment;
  const echoSubheader =
    mc !== null && mc !== undefined
      ? resolveEchoSubheader({
          role: message.role,
          isReply: Boolean(mc.parentComment?.id),
          isSentByLoggedInUser: mc.author?.auth0id === user?.sub,
          // Use auth0id for both sides so the equality check is
          // comparing the same ID namespace as user?.sub (Auth0 subject
          // ID). The relay global `.id` is a different namespace and
          // the comparison would always fail.
          mediaCreatedById: mc.media?.createdBy?.auth0id ?? null,
          parentAuthorId: mc.parentComment?.author?.auth0id ?? null,
          loggedInUserId: user?.sub ?? null,
          mediaStartTimeSeconds: mc.mediaStartTime,
        })
      : null;

  const deleteMessage = () => {
    if (!deleteMessageId) return;

    handleDelete(deleteMessageId, () => {
      setDeleteMessageId("");
      setOpenDeleteMessage(false);
    });
  };

  const handleSave = async () => {
    if (editedMessage.trim() === (message.content || "").trim()) {
      setIsEditing(false);
      return;
    }

    if (!editedMessage.trim()) {
      setIsEditing(false);
      setEditedMessage(message.content || "");
      return;
    }

    handleEdit(message.id, editedMessage.trim(), () => {
      setIsEditing(false);
    });
  };

  return (
    <div className={clsx("flex mb-6", isUser ? "justify-end" : "justify-start")}>
      <div className="max-w-2xl w-full">
        <div className={clsx("flex items-start space-x-3", isUser ? "flex-row-reverse space-x-reverse" : "")}>
          <div className="mt-4">
            {userFirstName && (
              <UserInitialsView
                className="h-8! w-8! min-h-8! min-w-8! text-[16px]! leading-4"
                firstName={userFirstName}
                lastName={userLastName || ""}
              />
            )}
          </div>

          <div className={clsx("flex-1 min-w-0", isUser && "text-right")}>
            <div className={clsx("flex flex-row gap-2 mb-2", isUser && "justify-end")}>
              {userFirstName && (
                <Paragraph size="extra-small">
                  {userFirstName} {userLastName}
                </Paragraph>
              )}
              <Paragraph size="extra-small">{format(new Date(message.createdDate), "h:mm a")}</Paragraph>
            </div>

            {/* Echo subheader — "Comment on video @ 4:45". Clickable: opens
                the chat-video panel for the source media. */}
            {echoSubheader && mc?.media?.id && (
              <div className={clsx("flex mb-1", isUser && "justify-end")}>
                <button
                  type="button"
                  onClick={() => onVideoClick?.(mc.media.id, mc.mediaStartTime ?? undefined, mc.id)}
                  className="inline-flex flex-row items-center gap-1 text-xs text-limestone-500 hover:text-teal-700 focus:outline-none"
                >
                  <span className="italic">{echoSubheader.prefix}</span>
                  <span className="italic text-teal-600">{echoSubheader.anchor}</span>
                </button>
              </div>
            )}

            <div className={clsx("flex flex-row w-full min-w-0 group", isUser && "gap-1")}>
              {isUser && !isEditing && <div className="flex-1 min-w-0" />}
              <div
                className={clsx(
                  "flex flex-col shrink-0 transition-opacity",
                  isMobile ? "opacity-100 visible" : "opacity-0 group-hover:opacity-100 invisible group-hover:visible",
                )}
              >
                {isUser && !isOlderThan15min && !isEditing && (
                  <PopoverMenu menuItems={menuItems} openDirection="to-left" />
                )}
              </div>
              <div
                className={clsx(
                  "rounded-lg min-w-0 max-w-full",
                  isEditing ? "flex-1 min-w-48" : "px-4 py-3",
                  isUser
                    ? "bg-limestonev2-300 text-[#0E2528] border text-left"
                    : "bg-white text-text-primary border border-limestone-200",
                )}
              >
                {/* Video thumbnail card */}
                {mediaVideo && (
                  <button
                    type="button"
                    onClick={() => onVideoClick?.(mediaVideo.id)}
                    className="relative w-full aspect-video rounded-t-lg overflow-hidden bg-limestone-200 group/video focus:outline-none"
                  >
                    {mediaVideo.thumbnailUrl ? (
                      <img src={mediaVideo.thumbnailUrl} alt="Video thumbnail" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-limestone-300" />
                    )}
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover/video:bg-black/40 transition-colors">
                      <PlayCircleIcon className="h-12 w-12 text-white drop-shadow" />
                    </div>
                    {mediaCommentsCount !== null && (
                      <div className="absolute bottom-2 right-2 flex items-center gap-1 bg-black/50 text-white text-xs px-2 py-0.5 rounded-full">
                        <ChatBubbleLeftIcon className="h-3 w-3" />
                        {mediaCommentsCount}
                      </div>
                    )}
                  </button>
                )}

                <div className={clsx("flex flex-col gap-2", isEditing && "bg-bg-default")} ref={editContainerRef}>
                  <ChatWYSIWYG
                    key={isEditing ? "editing" : "display"}
                    title=""
                    placeholder=""
                    existingContent={editedMessage}
                    onSave={async ({ content }) => {
                      setEditedMessage(content);
                    }}
                    isSaving={false}
                    autoSave={true}
                    autoSaveDebounce={0} // setting this to 0 basically creates an onChange effect
                    editMode={isEditing}
                    showTitle={false}
                    showAutoSaveMessage={false}
                    displayOnlyMode={!isEditing}
                    readOnlyTimerDelay={2147483647} // setting this to a large nmber keeps the tool bar open at all times
                    containerClassName="mt-0! mb-0! border-0"
                    inputContainerClassName="border bg-white m-6 min-h-[150px]"
                  />

                  {isEditing && (
                    <div className="flex flex-row gap-2 items-center justify-end pb-6 pr-6">
                      <Button
                        text="Cancel"
                        variant="primary"
                        buttonStyle="border"
                        onClick={() => {
                          setIsEditing(false);
                          setEditedMessage(message.content || "");
                        }}
                      />
                      <Button text="Save" variant="primary" buttonStyle="filled" onClick={handleSave} />
                    </div>
                  )}
                </div>

                {message.file && (
                  <div className="mt-2">
                    <ChatAttachment
                      fileName={message.file.originalFileName}
                      fileSize={message.file.fileSize}
                      mimeType={message.file.mimeType}
                      variant={isUser ? "sender" : "receiver"}
                      onClick={handleDownloadFile}
                    />
                  </div>
                )}
              </div>
            </div>

            <div className={clsx("flex flex-row gap-1 items-center", isUser && "justify-end")}>
              {isUser && (
                <>
                  {message.readBy && isLastMessage ? (
                    <Paragraph size="extra-small">
                      Read {message.readAt && format(new Date(message.readAt), "h:mm a")}
                    </Paragraph>
                  ) : (
                    <> {isLastMessage && <Paragraph size="extra-small">Delivered</Paragraph>}</>
                  )}
                </>
              )}
              {message.edited && (
                <Paragraph className="italic" size="extra-small">
                  Edited
                </Paragraph>
              )}
            </div>
          </div>
        </div>
      </div>

      <ConfirmationDialog
        open={openDeleteMessage}
        onCancelClick={() => {
          setDeleteMessageId("");
          setOpenDeleteMessage(false);
        }}
        onSubmitClick={() => {
          deleteMessage();
        }}
        title="Delete Message"
        content="Are you sure you want to delete this message? This action cannot be undone. If there are any attachments, these will also be removed."
        submitButtonText="Delete"
      />
    </div>
  );
};
